import { Injectable } from '@angular/core';
import { AppwriteApiEndpoint, AppwriteApiRoute, AppwriteBatchSetRequest, BatchDeleteEntityGroups, BatchSetEntityGroups, BatchUpdateEntityGroups, Collection, CollectionMap, fromAppwrite, generateCustomerSafeId, generateId, runAppwriteTransaction, toAppwrite } from '@prism/prism-shared';
import { Client, ExecutionMethod, Functions, ID, Query, TablesDB } from 'appwrite';
import { defer, finalize, firstValueFrom, forkJoin, from, map, mergeMap, Observable, of, shareReplay, switchMap, toArray } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { AppwriteClientService } from '../tokens/appwrite-client.token';

const REALTIME_CHANNEL_MODE: 'tablesdb' | 'legacy' = 'tablesdb';

type WhereOp =
    | '=='
    | '!='
    | '<'
    | '<='
    | '>'
    | '>='
    | 'in'
    | 'not-in'
    | 'array-contains'
    | 'array-contains-any';

export interface QueryRef {
    where(field: string, op: WhereOp, value: unknown): QueryRef;
    orderBy(field: string, direction?: 'asc' | 'desc'): QueryRef;
    limit(n: number): QueryRef;
}

export type QueryFn = (ref: QueryRef) => QueryRef;

export interface DocSnapshot<T> {
    id: string;
    exists: boolean;
    data(): T | undefined;
    ref?: {
        update(data: Partial<T>): Promise<unknown>;
        set(data: T): Promise<unknown>;
        delete(): Promise<unknown>;
    };
}

export interface QuerySnapshot<T> {
    docs: DocSnapshot<T>[];
    size: number;
    empty: boolean;
}

export interface AppwriteDocumentRef<T> {
    valueChanges(): Observable<T | undefined>;
    get(): Observable<DocSnapshot<T>>;
    set(data: T): Promise<void>;
    update(data: Partial<T> | Record<string, unknown>): Promise<void>;
    delete(): Promise<void>;
}

export interface AppwriteCollectionRef<T> {
    valueChanges(): Observable<T[]>;
    get(): Observable<QuerySnapshot<T>>;
    count(): Observable<number>;
    doc<TDoc = T>(id: string): AppwriteDocumentRef<TDoc>;
}

export function fromAppwriteDoc<T>(snap: DocSnapshot<T>): T {
    if (!snap.exists) {
        return null as unknown as T
    }
    return snap.data()!;
}

export function fromAppwriteDocs<T>(docs: DocSnapshot<T>[]): T[] {
    return docs.map(d => fromAppwriteDoc(d));
}

export const FieldValue = {
    increment: (n: number) => ({ __op: 'increment' as const, value: n }),
};

type FieldValueOp = ReturnType<typeof FieldValue.increment>;

type BatchSetOperation = {
    collection: Collection;
    doc: { id: string };
    action: 'create' | 'update' | 'upsert';
};
type BatchDeleteOperation = { collection: Collection; id: string };
export type BatchIncrementOperation = {
    collection: Collection;
    id: string;
    field: string;
    value: number;
    min?: number;
    max?: number;
    /** Set a boolean once the incremented counter reaches its interval, within this transaction. */
    thresholdLatch?: { flagColumn: string; baselineColumn: string; intervalColumn: string; defaultInterval: number;
        expected?: Record<string, unknown> };
};

function isFieldValueOp(v: unknown): v is FieldValueOp {
    return typeof v === 'object' && v !== null && '__op' in v;
}

function buildQueries(queryFn?: QueryFn): string[] {
    const queries: string[] = [];
    const ref: QueryRef = {
        where: (field, op, value) => {
            switch (op) {
                case '==':
                    queries.push(value === null ? Query.isNull(field) : Query.equal(field, value as never));
                    break;
                case '!=':
                    queries.push(value === null ? Query.isNotNull(field) : Query.notEqual(field, value as never));
                    break;
                case '<':
                    queries.push(Query.lessThan(field, value as never));
                    break;
                case '<=':
                    queries.push(Query.lessThanEqual(field, value as never));
                    break;
                case '>':
                    queries.push(Query.greaterThan(field, value as never));
                    break;
                case '>=':
                    queries.push(Query.greaterThanEqual(field, value as never));
                    break;
                case 'in':
                    queries.push(Query.equal(field, value as never));
                    break;
                case 'not-in':
                    queries.push(Query.notEqual(field, value as never));
                    break;
                case 'array-contains':
                    queries.push(Query.contains(field, value as never));
                    break;
                case 'array-contains-any':
                    queries.push(Query.containsAny(field, value as never));
                    break;
            }
            return ref;
        },
        orderBy: (field, direction = 'asc') => {
            queries.push(direction === 'desc' ? Query.orderDesc(field) : Query.orderAsc(field));
            return ref;
        },
        limit: (n) => {
            queries.push(Query.limit(n));
            return ref;
        },
    };
    if (queryFn) queryFn(ref);
    return queries;
}

function cloneRealtimeValue<T>(value: T): T {
    if (typeof structuredClone === 'function') {
        return structuredClone(value);
    }
    return JSON.parse(JSON.stringify(value)) as T;
}

function realtimeCollectionChannel(databaseId: string, collection: Collection | string): string {
    return REALTIME_CHANNEL_MODE === 'tablesdb'
        ? `tablesdb.${databaseId}.tables.${collection}.rows`
        : `databases.${databaseId}.collections.${collection}.documents`;
}

function realtimeDocumentChannel(databaseId: string, collection: Collection | string, id: string): string {
    return REALTIME_CHANNEL_MODE === 'tablesdb'
        ? `tablesdb.${databaseId}.tables.${collection}.rows.${id}`
        : `databases.${databaseId}.collections.${collection}.documents.${id}`;
}

@Injectable({ providedIn: 'root' })
export class AppwriteDatabaseService {
    private static readonly DEFAULT_QUERY_LIMIT = 5000;
    private static readonly BATCH_CONCURRENCY = 25;

    public readonly client: Client;
    private readonly tablesDb: TablesDB;
    private readonly collectionValueChangesCache = new Map<string, Observable<unknown>>();
    private readonly documentValueChangesCache = new Map<string, Observable<unknown>>();
    public readonly databaseId: string;
    private readonly endpoint: string;
    private readonly projectId: string;

    constructor(appwrite: AppwriteClientService) {
        this.client = appwrite.client;
        this.tablesDb = new TablesDB(this.client);
        this.databaseId = appwrite.config.databaseId;
        this.endpoint = appwrite.config.endpoint;
        this.projectId = appwrite.config.projectId;
    }

    public createId(): string {
        let chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let autoId = '';
        for (let i = 0; i < 20; i++) {
            autoId += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return autoId;
    }

    public createCustomerSafeId(): string {
        return generateCustomerSafeId(() => this.createId());
    }

    public collection<T>(
        name: Collection | string,
        queryFn?: QueryFn,
    ): AppwriteCollectionRef<T> {
        const queries = buildQueries(queryFn);
        const databaseId = this.databaseId;
        const endpointBase = this.endpoint;
        const projectId = this.projectId;
        const db = this;

        return {
            valueChanges: (): Observable<T[]> => {
                return db.getSharedCollectionValueChanges<T>(name, queries, () => new Observable<T[]>((subscriber) => {
                    let items: T[] = [];
                    let refreshTimer: ReturnType<typeof setTimeout> | null = null;
                    let socket: WebSocket | null = null;
                    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
                    let closedByTeardown = false;
                    let reconnectAttempt = 0;
                    const reconnectDelays = [1000, 2000, 5000, 10000, 30000];
                    const emitItems = () => {
                        subscriber.next(items.map(item => cloneRealtimeValue(item)));
                    };

                    const refresh = () => {
                        db.list<T>(name, queries).subscribe({
                            next: (resp) => {
                                items = resp.items;
                                emitItems();
                            },
                            error: (err) => subscriber.error(err),
                        });
                    };

                    refresh();

                    const channel = realtimeCollectionChannel(databaseId, name);
                    const endpoint = endpointBase
                        .replace(/^https:\/\//, 'wss://')
                        .replace(/^http:\/\//, 'ws://');
                    const url = `${endpoint}/realtime?project=${encodeURIComponent(projectId)}&channels[]=${encodeURIComponent(channel)}`;
                    const scheduleReconnect = () => {
                        if (reconnectTimer || closedByTeardown) {
                            return;
                        }
                        const delay = reconnectDelays[Math.min(reconnectAttempt, reconnectDelays.length - 1)];
                        reconnectAttempt++;
                        reconnectTimer = setTimeout(() => {
                            reconnectTimer = null;
                            if (closedByTeardown) {
                                return;
                            }
                            connect();
                        }, delay);
                    };
                    const connect = () => {
                        socket = new WebSocket(url);
                        socket.addEventListener('message', (event) => {
                            const message = JSON.parse(event.data);
                            if (message.type !== 'event') {
                                return;
                            }
                            const realtimeData = message.data ?? message;
                            const events: string[] = realtimeData.events ?? [];
                            const payload = realtimeData.payload;
                            reconnectAttempt = 0;
                            if (queries.length > 0) {
                                if (refreshTimer) clearTimeout(refreshTimer);
                                refreshTimer = setTimeout(refresh, 100);
                                return;
                            }
                            const doc = payload ? fromAppwrite<T>(name as Collection, payload) : null;
                            const ev = events.find(
                                (e: string) =>
                                    e.endsWith('.create') ||
                                    e.endsWith('.update') ||
                                    e.endsWith('.delete'),
                            );
                            if (!doc || !ev) {
                                if (refreshTimer) clearTimeout(refreshTimer);
                                refreshTimer = setTimeout(refresh, 100);
                                return;
                            }
                            const docId = (doc as unknown as { id: string }).id;
                            const idOf = (i: T): string => (i as unknown as { id: string }).id;
                            if (ev.endsWith('.create')) {
                                if (!items.some((i) => idOf(i) === docId)) items = [...items, doc];
                            } else if (ev.endsWith('.update')) {
                                items = items.map((i) => (idOf(i) === docId ? doc : i));
                            } else if (ev.endsWith('.delete')) {
                                items = items.filter((i) => idOf(i) !== docId);
                            }
                            emitItems();
                        });
                        socket.addEventListener('error', (err) => {
                            if (!closedByTeardown && name !== Collection.metalsTask) {
                                console.error('[appwrite realtime] collection socket error', { collection: name, channel, err });
                            }
                        });
                        socket.addEventListener('close', (event) => {
                            if (closedByTeardown) {
                                return;
                            }
                            if (name !== Collection.metalsTask) {
                                console.warn('[appwrite realtime] collection socket closed; reconnecting', {
                                    collection: name,
                                    channel,
                                    code: event.code,
                                    reason: event.reason,
                                    wasClean: event.wasClean,
                                });
                            }
                            refresh();
                            scheduleReconnect();
                        });
                    };
                    connect();

                    return () => {
                        closedByTeardown = true;
                        if (refreshTimer) clearTimeout(refreshTimer);
                        if (reconnectTimer) clearTimeout(reconnectTimer);
                        socket?.close();
                    };
                }));
            },

            get: (): Observable<QuerySnapshot<T>> => {
                return db.list<T>(name, queries).pipe(
                    map((resp) => {
                        const docs: DocSnapshot<T>[] = resp.items.map((item) => {
                            const id = (item as unknown as { id: string }).id;
                            return {
                                id,
                                exists: true,
                                data: () => item,
                                ref: {
                                    update: (data: Partial<T>) => db.collection<T>(name).doc(id).update(data),
                                    set: (data: T) => db.collection<T>(name).doc(id).set(data),
                                    delete: () => db.collection<T>(name).doc(id).delete(),
                                },
                            };
                        });
                        return {
                            docs,
                            size: docs.length,
                            empty: docs.length === 0,
                        };
                    }),
                );
            },

            count: (): Observable<number> => {
                return db.list<T>(name, queries).pipe(map(resp => resp.total));
            },

            doc: <TDoc = T>(id: string): AppwriteDocumentRef<TDoc> => ({
                valueChanges: (): Observable<TDoc | undefined> => {
                    return db.getSharedDocumentValueChanges<TDoc>(name, id, () => new Observable<TDoc | undefined>((subscriber) => {
                        let socket: WebSocket | null = null;
                        let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
                        let closedByTeardown = false;
                        let reconnectAttempt = 0;
                        const reconnectDelays = [1000, 2000, 5000, 10000, 30000];

                        db.get<TDoc>(name, id).subscribe({
                            next: (doc) => subscriber.next(doc),
                            error: (err: { code?: number }) => {
                                if (err?.code === 404) subscriber.next(undefined);
                                else subscriber.error(err);
                            },
                        });

                        const channel = realtimeDocumentChannel(databaseId, name, id);
                        const endpoint = endpointBase
                            .replace(/^https:\/\//, 'wss://')
                            .replace(/^http:\/\//, 'ws://');
                        const url = `${endpoint}/realtime?project=${encodeURIComponent(projectId)}&channels[]=${encodeURIComponent(channel)}`;
                        const refresh = () => {
                            db.get<TDoc>(name, id).subscribe({
                                next: (doc) => subscriber.next(doc),
                                error: (err: { code?: number }) => {
                                    if (err?.code === 404) subscriber.next(undefined);
                                    else console.error('[appwrite realtime] document refresh failed after socket close', { collection: name, id, err });
                                },
                            });
                        };
                        const scheduleReconnect = () => {
                            if (reconnectTimer || closedByTeardown) {
                                return;
                            }
                            const delay = reconnectDelays[Math.min(reconnectAttempt, reconnectDelays.length - 1)];
                            reconnectAttempt++;
                            reconnectTimer = setTimeout(() => {
                                reconnectTimer = null;
                                if (closedByTeardown) {
                                    return;
                                }
                                connect();
                            }, delay);
                        };
                        const connect = () => {
                            socket = new WebSocket(url);
                            socket.addEventListener('message', (event) => {
                                const message = JSON.parse(event.data);
                                if (message.type !== 'event') {
                                    return;
                                }
                                reconnectAttempt = 0;
                                const ev = message.data.events.find(
                                    (e: string) =>
                                        e.endsWith('.create') ||
                                        e.endsWith('.update') ||
                                        e.endsWith('.delete'),
                                );
                                if (ev?.endsWith('.delete')) {
                                    subscriber.next(undefined);
                                } else {
                                    subscriber.next(fromAppwrite<TDoc>(name as Collection, message.data.payload));
                                }
                            });
                            socket.addEventListener('error', (err) => {
                                if (!closedByTeardown && name !== Collection.metalsTask) {
                                    console.error('[appwrite realtime] document socket error', { collection: name, id, channel, err });
                                }
                            });
                            socket.addEventListener('close', (event) => {
                                if (closedByTeardown) {
                                    return;
                                }
                                if (name !== Collection.metalsTask) {
                                    console.warn('[appwrite realtime] document socket closed; reconnecting', {
                                        collection: name,
                                        id,
                                        channel,
                                        code: event.code,
                                        reason: event.reason,
                                        wasClean: event.wasClean,
                                    });
                                }
                                refresh();
                                scheduleReconnect();
                            });
                        };
                        connect();

                        return () => {
                            closedByTeardown = true;
                            if (reconnectTimer) clearTimeout(reconnectTimer);
                            socket?.close();
                        };
                    }));
                },

                get: (): Observable<DocSnapshot<TDoc>> => {
                    return db.get<TDoc>(name, id).pipe(
                        map((doc): DocSnapshot<TDoc> => ({
                            id,
                            exists: true,
                            data: () => doc,
                            ref: {
                                update: (data: Partial<TDoc>) => db.collection<TDoc>(name).doc(id).update(data),
                                set: (data: TDoc) => db.collection<TDoc>(name).doc(id).set(data),
                                delete: () => db.collection<TDoc>(name).doc(id).delete(),
                            },
                        })),
                        catchError((err: { code?: number }) => {
                            if (err?.code === 404) {
                                const empty: DocSnapshot<TDoc> = {
                                    id,
                                    exists: false,
                                    data: () => undefined,
                                    ref: {
                                        update: (data: Partial<TDoc>) => db.collection<TDoc>(name).doc(id).update(data),
                                        set: (data: TDoc) => db.collection<TDoc>(name).doc(id).set(data),
                                        delete: () => db.collection<TDoc>(name).doc(id).delete(),
                                    },
                                };
                                return of(empty);
                            }
                            throw err;
                        }),
                    );
                },

                set: (data: TDoc): Promise<void> => firstValueFrom(db.set(name, id, data)),

                update: (data: Partial<TDoc> | Record<string, unknown>): Promise<void> =>
                    firstValueFrom(db.update<TDoc>(name, id, data)),

                delete: (): Promise<void> => firstValueFrom(db.delete(name, id)),
            }),
        };
    }

    // -------- batch operations --------

    public batchSet<C extends Collection>(
        entities: (CollectionMap[C] & { id: string })[],
        collection: C,
    ): Observable<void> {
        if (entities.length === 0) return of(undefined);
        return from(entities).pipe(
            mergeMap(
                (entity) => this.set(collection, entity.id, entity as { id: string }),
                AppwriteDatabaseService.BATCH_CONCURRENCY,
            ),
            toArray(),
            map(() => undefined),
        );
    }

    public batchSetAcrossCollections(groups: BatchSetEntityGroups): Observable<void> {
        const operations = this.flattenBatchSetGroups(groups);
        if (operations.length === 0) return of(undefined);
        return from(operations).pipe(
            mergeMap(
                ({ collection, doc }) => this.set(collection, doc.id, doc),
                AppwriteDatabaseService.BATCH_CONCURRENCY,
            ),
            toArray(),
            map(() => undefined),
        );
    }

    public batchSetAcrossCollectionsTransactional(groups: BatchSetEntityGroups): Observable<void> {
        const setOperations = this.flattenBatchSetGroups(groups);
        if (setOperations.length === 0) return of(void 0);
        return from(this.runTransactionalBatch(setOperations, []));
    }

    /** Save serialized rows atomically through the generic, permission-checked bulk API. */
    public batchSetRowsAcrossCollectionsTransactional(request: AppwriteBatchSetRequest): Observable<void> {
        const rowCount = request.groups.reduce((count, group) => count + group.rows.length, 0);
        if (!rowCount) return of(void 0);
        return defer(async () => {
            const functions = new Functions(this.client);
            if (request.guards?.length) {
                // Older deployments ignore unknown request fields. Probe without rows first
                // so a guarded bulk write never silently falls back to an unguarded commit.
                const probe = await functions.createExecution({
                    functionId: 'api',
                    xpath: `/${AppwriteApiRoute.Database}/${AppwriteApiEndpoint.BatchSet}`,
                    method: ExecutionMethod.POST, async: false,
                    body: JSON.stringify({ groups: [], checkCapabilities: true }),
                    headers: { 'content-type': 'application/json' }
                });
                let capabilities: { readGuards?: boolean } = {};
                try { capabilities = JSON.parse(probe.responseBody); } catch { /* Fail closed below. */ }
                if (probe.status !== 'completed' || probe.responseStatusCode >= 400 || capabilities.readGuards !== true) {
                    throw Object.assign(new Error('This large atomic save requires the updated generic batch API. No data was saved.'), { statusCode: 503 });
                }
            }
            return functions.createExecution({
                functionId: 'api',
                xpath: `/${AppwriteApiRoute.Database}/${AppwriteApiEndpoint.BatchSet}`,
                method: ExecutionMethod.POST,
                async: false,
                body: JSON.stringify(request),
                headers: { 'content-type': 'application/json' },
            });
        }).pipe(map(execution => {
            let body: { saved?: number; message?: string; error?: string };
            try { body = JSON.parse(execution.responseBody); } catch { body = {}; }
            if (execution.status !== 'completed' || execution.responseStatusCode >= 400) {
                const message = body.message || (body.error === 'not_found'
                    ? 'Batch saving is unavailable. Update the API function and try again.'
                    : body.error) || execution.errors || execution.responseBody || 'Unable to save the batch.';
                throw Object.assign(new Error(message), { statusCode: execution.responseStatusCode, code: body.error, body });
            }
            if (body.saved !== rowCount) throw new Error('The batch-save response could not be verified. Refresh before retrying.');
        }));
    }

    public batchUpdateAcrossCollectionsTransactional(groups: BatchUpdateEntityGroups): Observable<void> {
        const updateOperations = this.flattenBatchSetGroups(groups, 'update');
        if (updateOperations.length === 0) return of(void 0);
        return from(this.runTransactionalBatch(updateOperations, []));
    }

    public batchSetAndIncrementAcrossCollectionsTransactional(
        setGroups: BatchSetEntityGroups,
        incrementOperations: BatchIncrementOperation[],
    ): Observable<void> {
        const setOperations = this.flattenBatchSetGroups(setGroups);
        const filteredIncrementOperations = incrementOperations.filter(operation => operation.value !== 0);
        if (setOperations.length === 0 && filteredIncrementOperations.length === 0) return of(void 0);
        return from(this.runTransactionalBatch(setOperations, [], filteredIncrementOperations));
    }

    public batchCreateSetAndIncrementAcrossCollectionsTransactional(
        createGroups: BatchSetEntityGroups,
        setGroups: BatchSetEntityGroups,
        incrementOperations: BatchIncrementOperation[],
    ): Observable<void> {
        const createOperations = this.flattenBatchSetGroups(createGroups, 'create');
        const setOperations = this.flattenBatchSetGroups(setGroups);
        const filteredIncrementOperations = incrementOperations.filter(operation => operation.value !== 0);
        if (
            createOperations.length === 0
            && setOperations.length === 0
            && filteredIncrementOperations.length === 0
        ) {
            return of(void 0);
        }
        return from(this.runTransactionalBatch(
            [...createOperations, ...setOperations],
            [],
            filteredIncrementOperations,
        ));
    }

    public batchCreateUpdateAndIncrementAcrossCollectionsTransactional(
        createGroups: BatchSetEntityGroups,
        updateGroups: BatchUpdateEntityGroups,
        incrementOperations: BatchIncrementOperation[],
    ): Observable<void> {
        const createOperations = this.flattenBatchSetGroups(createGroups, 'create');
        const updateOperations = this.flattenBatchSetGroups(updateGroups, 'update');
        const filteredIncrementOperations = incrementOperations.filter(operation => operation.value !== 0);
        if (
            createOperations.length === 0
            && updateOperations.length === 0
            && filteredIncrementOperations.length === 0
        ) {
            return of(void 0);
        }
        return from(this.runTransactionalBatch(
            [...createOperations, ...updateOperations],
            [],
            filteredIncrementOperations,
        ));
    }

    public batchDelete<C extends Collection>(
        entityIds: string[],
        collection: C,
    ): Observable<void> {
        if (entityIds.length === 0) return of(undefined);
        return from(entityIds).pipe(
            mergeMap((id) => this.delete(collection, id), AppwriteDatabaseService.BATCH_CONCURRENCY),
            toArray(),
            map(() => undefined),
        );
    }

    public batchDeleteAcrossCollections(entities: BatchDeleteEntityGroups): Observable<void> {
        const operations = this.flattenBatchDeleteGroups(entities);
        if (operations.length === 0) return of(undefined);
        return from(operations).pipe(
            mergeMap(
                ({ collection, id }) => this.delete(collection, id),
                AppwriteDatabaseService.BATCH_CONCURRENCY,
            ),
            toArray(),
            map(() => undefined),
        );
    }

    public batchSetAndDeleteAcrossCollections(
        setGroups: BatchSetEntityGroups,
        deleteGroups: BatchDeleteEntityGroups,
    ): Observable<void> {
        const hasSets = Object.values(setGroups).some(docs => !!docs?.length);
        const hasDeletes = Object.values(deleteGroups).some(ids => !!ids?.length);
        if (!hasSets && !hasDeletes) return of(void 0);
        return forkJoin([
            hasSets ? this.batchSetAcrossCollections(setGroups) : of(void 0),
            hasDeletes ? this.batchDeleteAcrossCollections(deleteGroups) : of(void 0),
        ]).pipe(map(() => void 0));
    }

    public batchSetAndDeleteAcrossCollectionsTransactional(
        setGroups: BatchSetEntityGroups,
        deleteGroups: BatchDeleteEntityGroups,
    ): Observable<void> {
        const setOperations = this.flattenBatchSetGroups(setGroups);
        const deleteOperations = this.flattenBatchDeleteGroups(deleteGroups);
        if (setOperations.length === 0 && deleteOperations.length === 0) return of(void 0);
        return from(this.runTransactionalBatch(setOperations, deleteOperations));
    }

    private flattenBatchSetGroups(
        groups: BatchSetEntityGroups | BatchUpdateEntityGroups,
        action: BatchSetOperation['action'] = 'upsert',
    ): BatchSetOperation[] {
        const operations: BatchSetOperation[] = [];
        (Object.keys(groups) as Collection[]).forEach((collection) => {
            const docs = groups[collection];
            if (!docs) return;
            docs.forEach((doc) => operations.push({ collection, doc, action }));
        });
        return operations;
    }

    private flattenBatchDeleteGroups(groups: BatchDeleteEntityGroups): BatchDeleteOperation[] {
        const operations: BatchDeleteOperation[] = [];
        for (const [key, ids] of Object.entries(groups)) {
            if (!ids) continue;
            for (const id of ids) {
                operations.push({ collection: key as Collection, id });
            }
        }
        return operations;
    }

    private async runTransactionalBatch(
        setOperations: BatchSetOperation[],
        deleteOperations: BatchDeleteOperation[],
        incrementOperations: BatchIncrementOperation[] = [],
    ): Promise<void> {
        if (setOperations.length > 100 && !deleteOperations.length && !incrementOperations.length
            && setOperations.every(operation => operation.action !== 'update')) {
            const request: AppwriteBatchSetRequest = { groups: [] };
            for (const { collection, doc, action } of setOperations) {
                let group = request.groups.find(candidate => candidate.collection === collection && candidate.action === action);
                if (!group) {
                    group = { collection, action: action as 'create' | 'upsert', rows: [] };
                    request.groups.push(group);
                }
                group.rows.push({
                    id: doc.id,
                    data: toAppwrite(collection, collection === Collection.globalVariables ? { ...doc, kind: doc.id } : doc),
                });
            }
            await firstValueFrom(this.batchSetRowsAcrossCollectionsTransactional(request));
            return;
        }
        await runAppwriteTransaction(this.tablesDb, async transactionId => {
            for (const { collection, doc, action } of setOperations) {
                const payload = toAppwrite(
                    collection,
                    collection === Collection.globalVariables
                        ? { ...(doc as Record<string, unknown>), kind: doc.id }
                        : doc,
                );

                const rowOperation = {
                    databaseId: this.databaseId,
                    tableId: collection,
                    rowId: doc.id,
                    data: payload,
                    transactionId,
                };

                // Stage row operations individually so Appwrite preserves the
                // explicit row ID. The multi-operation endpoint generated new
                // IDs for these upserts on the deployed self-hosted instance.
                switch (action) {
                    case 'create':
                        await this.tablesDb.createRow({
                            ...rowOperation,
                            permissions: [],
                        });
                        break;
                    case 'update':
                        await this.tablesDb.updateRow(rowOperation);
                        break;
                    case 'upsert':
                        await this.tablesDb.upsertRow({
                            ...rowOperation,
                            permissions: [],
                        });
                        break;
                }
            }

            const latchGuards: Array<{ collection: Collection; id: string; updatedAt: string }> = [];
            for (const { collection, id, field, value, min, max, thresholdLatch } of incrementOperations) {
                let latchSource: Record<string, unknown> | undefined;
                if (thresholdLatch) {
                    if (setOperations.some(operation => operation.collection === collection && operation.doc.id === id)
                        || incrementOperations.filter(operation => operation.collection === collection && operation.id === id && operation.field === field).length !== 1) {
                        throw new Error('Combine each threshold counter into one increment without a separate row set.');
                    }
                    const original = await this.tablesDb.getRow({ databaseId: this.databaseId, tableId: collection, rowId: id });
                    latchSource = original;
                    for (const [key, expected] of Object.entries(thresholdLatch.expected ?? {})) {
                        if (JSON.stringify(original[key]) !== JSON.stringify(expected)) throw new Error('The machine mold changed. Refresh before recording another cycle.');
                    }
                    latchGuards.push({ collection, id, updatedAt: original.$updatedAt });
                }
                if (value > 0) {
                    const counterCeiling = thresholdLatch && latchSource ? Number(latchSource[field]) + value : undefined;
                    if (counterCeiling !== undefined && !Number.isSafeInteger(counterCeiling)) throw new Error('Invalid counter; the cycle was not saved.');
                    await this.tablesDb.incrementRowColumn({
                        databaseId: this.databaseId,
                        tableId: collection,
                        rowId: id,
                        column: field,
                        value,
                        // Native increments can otherwise merge concurrent transactions without a conflict.
                        // This bound rejects a second increment based on the same counter snapshot at commit.
                        max: counterCeiling === undefined ? max : Math.min(max ?? counterCeiling, counterCeiling),
                        transactionId,
                    });
                    if (thresholdLatch && latchSource) {
                        const { flagColumn, baselineColumn, intervalColumn, defaultInterval } = thresholdLatch;
                        // Appwrite 1.9 staging returns the committed row, including on transaction reads.
                        // Derive the new count from our guarded fresh snapshot and the staged delta.
                        const count = Number(latchSource[field]) + value, baseline = Number(latchSource[baselineColumn] ?? 0);
                        const interval = Number(latchSource[intervalColumn] ?? defaultInterval);
                        if (!Number.isSafeInteger(count) || !Number.isSafeInteger(baseline) || !Number.isSafeInteger(interval) || interval <= 0) {
                            throw new Error('Invalid counter or interval; the cycle was not saved.');
                        }
                        if (latchSource[flagColumn] !== true && count - baseline >= interval) {
                            await this.tablesDb.updateRow({ databaseId: this.databaseId, tableId: collection, rowId: id,
                                data: { [flagColumn]: true }, transactionId });
                        }
                    }
                } else if (value < 0) {
                    await this.tablesDb.decrementRowColumn({
                        databaseId: this.databaseId,
                        tableId: collection,
                        rowId: id,
                        column: field,
                        value: Math.abs(value),
                        min,
                        transactionId,
                    });
                }
            }

            for (const guard of latchGuards) {
                const current = await this.tablesDb.getRow({ databaseId: this.databaseId, tableId: guard.collection, rowId: guard.id });
                if (current.$updatedAt !== guard.updatedAt) throw new Error('The mold or cycle count changed. Refresh and retry the cycle.');
            }

            for (const { collection, id } of deleteOperations) {
                await this.tablesDb.deleteRow({
                    databaseId: this.databaseId,
                    tableId: collection,
                    rowId: id,
                    transactionId,
                });
            }
        });
    }

    // -------- shared realtime cache --------

    private getSharedCollectionValueChanges<T>(
        collection: Collection | string,
        queries: string[],
        createSource: () => Observable<T[]>,
    ): Observable<T[]> {
        const key = this.collectionValueChangesKey(collection, queries);
        const existing = this.collectionValueChangesCache.get(key);
        if (existing) {
            return existing as Observable<T[]>;
        }

        // Firebase/AngularFire internally reused listeners aggressively enough that
        // Prism code often derived several subscriptions from the same valueChanges
        // method without an explicit shareReplay. Our Appwrite implementation is a
        // cold Observable: every subscription would otherwise run a REST seed, open
        // a websocket, and own reconnect/refresh timers. Keep this sharing internal
        // so migrated callers keep Firebase-like semantics.
        const shared$ = createSource().pipe(
            finalize(() => this.collectionValueChangesCache.delete(key)),
            shareReplay({ bufferSize: 1, refCount: true }),
        );
        this.collectionValueChangesCache.set(key, shared$);
        return shared$;
    }

    private getSharedDocumentValueChanges<T>(
        collection: Collection | string,
        id: string,
        createSource: () => Observable<T | undefined>,
    ): Observable<T | undefined> {
        const key = this.documentValueChangesKey(collection, id);
        const existing = this.documentValueChangesCache.get(key);
        if (existing) {
            return existing as Observable<T | undefined>;
        }

        const shared$ = createSource().pipe(
            finalize(() => this.documentValueChangesCache.delete(key)),
            shareReplay({ bufferSize: 1, refCount: true }),
        );
        this.documentValueChangesCache.set(key, shared$);
        return shared$;
    }

    private collectionValueChangesKey(collection: Collection | string, queries: string[]): string {
        return `${collection}::${queries.join('|')}`;
    }

    private documentValueChangesKey(collection: Collection | string, id: string): string {
        return `${collection}::${id}`;
    }

    // -------- primitives --------

    public create<T extends { id: string }>(collection: Collection | string, data: T): Observable<void> {
        const payload = toAppwrite(
            collection as Collection,
            collection === Collection.globalVariables ? { ...(data as Record<string, unknown>), kind: data.id } : data,
        );
        return from(this.tablesDb.createRow({
            databaseId: this.databaseId,
            tableId: collection,
            rowId: data.id,
            data: payload,
            permissions: [],
        })).pipe(
            map(() => void 0),
        );
    }

    /**
     * Creates a row and returns Appwrite's table-local monotonic sequence number.
     * This is useful for display numbers that must not race or be reused after deletions.
     */
    public createAndGetSequence<T extends { id: string }>(collection: Collection | string, data: T): Observable<number> {
        const payload = toAppwrite(
            collection as Collection,
            collection === Collection.globalVariables ? { ...(data as Record<string, unknown>), kind: data.id } : data,
        );
        return from(this.tablesDb.createRow({
            databaseId: this.databaseId,
            tableId: collection,
            rowId: data.id,
            data: payload,
            permissions: [],
        })).pipe(
            map((row) => Number((row as unknown as { $sequence: number }).$sequence)),
        );
    }

    /**
     * Atomically adds to one numeric column and returns the value AFTER the
     * add. The server does the arithmetic, so two callers racing each other get
     * two different results - which is what makes this safe for handing out
     * sequence numbers that must never be issued twice.
     *
     * Unlike `createAndGetSequence`, this does not need a row to be created, so
     * a number can be claimed at any point in an entity's life rather than only
     * at its creation.
     */
    public incrementAndRead(collection: Collection | string, id: string, field: string, by = 1): Observable<number> {
        return from(this.tablesDb.incrementRowColumn({
            databaseId: this.databaseId,
            tableId: collection,
            rowId: id,
            column: field,
            value: by,
        })).pipe(
            map(row => Number((row as unknown as Record<string, unknown>)[field])),
        );
    }

    public set<T>(collection: Collection | string, id: string, data: T): Observable<void> {
        const payload = toAppwrite(
            collection as Collection,
            collection === Collection.globalVariables ? { ...(data as Record<string, unknown>), kind: id } : data,
        );
        return from(this.tablesDb.upsertRow({
            databaseId: this.databaseId,
            tableId: collection,
            rowId: id,
            data: payload,
            permissions: [],
        })).pipe(
            map(() => void 0),
        );
    }

    public setExistingOrCreate<T>(collection: Collection | string, id: string, data: T): Observable<void> {
        return this.set(collection, id, data);
    }

    public update<T>(
        collection: Collection | string,
        id: string,
        data: Partial<T> | Record<string, unknown>,
    ): Observable<void> {
        const dataRecord = data as Record<string, unknown>;
        const hasFieldValueOps = Object.values(dataRecord).some(v => isFieldValueOp(v));
        if (hasFieldValueOps) {
            return this.get<Record<string, unknown>>(collection, id).pipe(
                switchMap((current) => {
                    const resolved: Record<string, unknown> = {};
                    for (const [key, value] of Object.entries(dataRecord)) {
                        if (isFieldValueOp(value) && value.__op === 'increment') {
                            resolved[key] = ((current[key] as number) ?? 0) + value.value;
                        } else {
                            resolved[key] = value;
                        }
                    }
                    const payload = toAppwrite(collection as Collection, resolved);
                    return from(this.tablesDb.updateRow({
                        databaseId: this.databaseId,
                        tableId: collection,
                        rowId: id,
                        data: payload,
                    })).pipe(map(() => void 0));
                }),
            );
        }
        const payload = toAppwrite(collection as Collection, data);
        return from(this.tablesDb.updateRow({
            databaseId: this.databaseId,
            tableId: collection,
            rowId: id,
            data: payload,
        })).pipe(
            map(() => void 0),
        );
    }

    public get<T>(collection: Collection | string, id: string): Observable<T> {
        return from(this.tablesDb.getRow({
            databaseId: this.databaseId,
            tableId: collection,
            rowId: id,
        })).pipe(
            map(doc => fromAppwrite<T>(collection as Collection, doc)),
        );
    }

    public list<T>(collection: Collection | string, queries: string[] = []): Observable<{ items: T[]; total: number }> {
        const effectiveQueries = this.withDefaultLimit(queries);
        return from(this.tablesDb.listRows({
            databaseId: this.databaseId,
            tableId: collection,
            queries: effectiveQueries,
        })).pipe(
            map(resp => ({
                items: resp.rows.map(d => fromAppwrite<T>(collection as Collection, d)),
                total: resp.total,
            })),
        );
    }

    public listAll<T>(collection: Collection | string, queries: string[] = [], pageSize = AppwriteDatabaseService.DEFAULT_QUERY_LIMIT): Observable<T[]> {
        const loadPage = (cursorAfterId?: string): Observable<T[]> => {
            const pageQueries = [
                ...queries,
                Query.orderAsc('$id'),
                Query.limit(pageSize),
                ...(cursorAfterId ? [Query.cursorAfter(cursorAfterId)] : []),
            ];

            return this.list<T>(collection, pageQueries).pipe(
                switchMap((resp) => {
                    if (resp.items.length < pageSize) {
                        return of(resp.items);
                    }

                    const lastItemId = (resp.items[resp.items.length - 1] as { id?: string }).id;
                    if (!lastItemId) {
                        return of(resp.items);
                    }

                    return loadPage(lastItemId).pipe(
                        map((nextItems) => [...resp.items, ...nextItems]),
                    );
                }),
            );
        };

        return loadPage();
    }

    private batchList<T>(
        collection: Collection | string,
        values: string[],
        buildQueries: (batch: string[]) => string[],
        batchSize = 30,
    ): Observable<T[]> {
        const batches = this.toValueBatches(values, batchSize);
        if (batches.length === 0) {
            return of([]);
        }

        return forkJoin(batches.map(batch => this.list<T>(collection, buildQueries(batch)).pipe(
            map(resp => resp.items),
        ))).pipe(
            map(groups => groups.flat()),
        );
    }

    public listByIds<T>(collection: Collection | string, ids: string[], batchSize = 30): Observable<T[]> {
        return this.batchList<T>(
            collection,
            ids,
            batch => [
                Query.equal('$id', batch),
                Query.limit(batch.length),
            ],
            batchSize,
        );
    }

    public listByFieldValues<T>(
        collection: Collection | string,
        field: string,
        values: string[],
        extraQueries: string[] = [],
        batchSize = 30,
        limitForBatch?: number | ((batch: string[]) => number),
    ): Observable<T[]> {
        return this.batchList<T>(
            collection,
            values,
            batch => {
                const limit = typeof limitForBatch === 'function' ? limitForBatch(batch) : limitForBatch;
                return [
                    Query.equal(field, batch),
                    ...extraQueries,
                    ...(limit === undefined ? [] : [Query.limit(limit)]),
                ];
            },
            batchSize,
        );
    }

    public delete(collection: Collection | string, id: string): Observable<void> {
        return from(this.tablesDb.deleteRow({
            databaseId: this.databaseId,
            tableId: collection,
            rowId: id,
        })).pipe(
            map(() => void 0),
        );
    }

    private toValueBatches(values: string[], batchSize: number): string[][] {
        const uniqueValues = Array.from(new Set(values.filter(value => !!value)));
        const batches: string[][] = [];
        for (let i = 0; i < uniqueValues.length; i += batchSize) {
            batches.push(uniqueValues.slice(i, i + batchSize));
        }
        return batches;
    }

    private withDefaultLimit(queries: string[]): string[] {
        if (queries.some(query => query.includes('"method":"limit"'))) {
            return queries;
        }
        return [...queries, Query.limit(AppwriteDatabaseService.DEFAULT_QUERY_LIMIT)];
    }
}
